import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { Toaster } from './components/ui/sonner';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Finance } from './pages/Finance';
import { ClientsSuppliers } from './pages/ClientsSuppliers';
import { HRM } from './pages/HRM';
import { Inventory } from './pages/Inventory';
import { Invoicing } from './pages/Invoicing';
import { Outsourcing } from './pages/Outsourcing';
import { OutsourcingWorker } from './pages/OutsourcingWorker';
import { Settings } from './pages/Settings';
import { Reports } from './pages/Reports';
import { ClientDetail } from './components/finance/ClientDetail';
import { SupplierDetail } from './components/finance/SupplierDetail';
import { canAccessPage, getDefaultRedirect } from './utils/permissions';
import './App.css';

const TopBar = () => {
  const { logout, realUser } = useAuth();
  const { t, toggleLanguage, language } = useLanguage();
  const navigate = useNavigate();
  return (
    <div className="sticky top-0 z-40 h-12 bg-primary text-primary-foreground flex items-center justify-between px-4 shrink-0">
      <button onClick={() => navigate('/dashboard')} className="font-bold text-lg tracking-wide hover:opacity-80 transition-opacity">
        CLADAKIS
      </button>
      <div className="flex items-center gap-1">
        <button onClick={toggleLanguage} className="text-sm px-3 py-1 rounded hover:bg-white/10 transition-colors" data-testid="language-toggle-btn">
          {language === 'ar' ? 'EN' : 'AR'}
        </button>
        <button onClick={logout} className="text-sm px-3 py-1 rounded hover:bg-white/10 transition-colors">
          {t('logout')}
        </button>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { realUser, loading, viewAsRole, setViewAsRole } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!realUser) {
    return <Navigate to="/" replace />;
  }

  if (!canAccessPage(realUser.role, location.pathname)) {
    return <Navigate to={getDefaultRedirect(realUser.role)} replace />;
  }

  return (
    <div className="flex flex-col min-h-screen">
      <TopBar />
      {viewAsRole && (
        <div className="sticky top-12 z-30 bg-amber-400 text-amber-900 text-xs font-medium py-1.5 px-4 flex justify-between items-center shrink-0">
          <span>Preview mode: viewing as <strong>{viewAsRole.replace(/_/g, ' ')}</strong></span>
          <button onClick={() => setViewAsRole(null)} className="underline hover:no-underline">Exit preview</button>
        </div>
      )}
      <div className="flex-1 overflow-auto">{children}</div>
    </div>
  );
};

function AppRoutes() {
  const { realUser } = useAuth();

  return (
    <Routes>
      <Route path="/" element={realUser ? <Navigate to={getDefaultRedirect(realUser.role)} replace /> : <Login />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance"
        element={
          <ProtectedRoute>
            <Finance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients-suppliers"
        element={
          <ProtectedRoute>
            <ClientsSuppliers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients-suppliers/client/:id"
        element={
          <ProtectedRoute>
            <ClientDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients-suppliers/supplier/:id"
        element={
          <ProtectedRoute>
            <SupplierDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hrm/*"
        element={
          <ProtectedRoute>
            <HRM />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/*"
        element={
          <ProtectedRoute>
            <Inventory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoicing/*"
        element={
          <ProtectedRoute>
            <Invoicing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/outsourcing"
        element={
          <ProtectedRoute>
            <Outsourcing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/outsourcing/worker/:id"
        element={
          <ProtectedRoute>
            <OutsourcingWorker />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;